import { Variants } from 'framer-motion';

// Page transition variants
export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.4,
      ease: 'easeIn',
    },
  },
};

// Card animation variants
export const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  hover: {
    scale: 1.02,
    y: -5,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

// Button animation variants
export const buttonVariants: Variants = {
  idle: {
    scale: 1,
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: 'easeIn',
    },
  },
};

// Text reveal variants
export const textRevealVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

// Stagger container variants
export const staggerContainerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

// Stagger item variants
export const staggerItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

// Glow animation variants
export const glowVariants: Variants = {
  idle: {
    boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
  },
  hover: {
    boxShadow: '0 0 40px rgba(0, 212, 255, 0.6)',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

// Loading spinner variants
export const spinnerVariants: Variants = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: 'linear',
    },
  },
};

// Pulse animation variants
export const pulseVariants: Variants = {
  animate: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Slide in variants
export const slideInVariants = {
  left: {
    hidden: {
      opacity: 0,
      x: -50,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  },
  right: {
    hidden: {
      opacity: 0,
      x: 50,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  },
  top: {
    hidden: {
      opacity: 0,
      y: -50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  },
  bottom: {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  },
};

// Fade in variants
export const fadeInVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

// Scale animation variants
export const scaleVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

// Error shake animation
export const shakeVariants: Variants = {
  shake: {
    x: [-10, 10, -10, 10, -5, 5, 0],
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
    },
  },
};

// Success bounce animation
export const bounceVariants: Variants = {
  bounce: {
    y: [0, -20, 0],
    scale: [1, 1.1, 1],
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

// Floating animation for background elements
export const floatingVariants: Variants = {
  animate: {
    y: [0, -10, 0],
    x: [0, 5, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Typewriter effect variants
export const typewriterVariants: Variants = {
  hidden: {
    width: 0,
  },
  visible: {
    width: '100%',
    transition: {
      duration: 2,
      ease: 'easeInOut',
    },
  },
};

// Neon glow variants
export const neonVariants: Variants = {
  idle: {
    textShadow: '0 0 10px rgba(0, 212, 255, 0.5)',
  },
  glow: {
    textShadow: [
      '0 0 10px rgba(0, 212, 255, 0.5)',
      '0 0 20px rgba(0, 212, 255, 0.8)',
      '0 0 30px rgba(0, 212, 255, 1)',
      '0 0 20px rgba(0, 212, 255, 0.8)',
      '0 0 10px rgba(0, 212, 255, 0.5)',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Export animation timing constants
export const animationTimings = {
  fast: 0.2,
  normal: 0.3,
  slow: 0.6,
  slower: 1.0,
  stagger: 0.1,
};

export const easingFunctions = {
  easeOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
};

// Named default export via variable to satisfy lint rule
const motionVariants = {
  pageVariants,
  cardVariants,
  buttonVariants,
  textRevealVariants,
  staggerContainerVariants,
  staggerItemVariants,
  glowVariants,
  spinnerVariants,
  pulseVariants,
  slideInVariants,
  fadeInVariants,
  scaleVariants,
  shakeVariants,
  bounceVariants,
  floatingVariants,
  typewriterVariants,
  neonVariants,
  animationTimings,
  easingFunctions,
};

export default motionVariants;